import { cn } from '@/lib/utils';
import { InputMasked } from '@chiroup/components/form/InputMasked';
import {
  spineParts,
  SpineParts,
  SpinePartsSelected,
  spinePartsTopPercent,
  SpineSide,
} from '@chiroup/core/functions/bodyData';
import { titleCase } from '@chiroup/core/functions/titleCase';
import { useEffect, useId, useState } from 'react';
import spine from '../../assets/images/spine.png';
import Modal from './Modal';

type Props = {
  value: SpinePartsSelected;
  onChange: (part: SpineParts, value?: SpineSide, amount?: number) => void;
  signed?: boolean;
  /** Hides the Leg Length Inequality control (trial SOAP sandbox). */
  hideLegLengthInequality?: boolean;
};

export const Spine: React.FC<Props> = ({ value, onChange, signed, hideLegLengthInequality }) => {
  const id = useId();
  const [selected, setSelected] = useState<SpineParts | null>(null);
  const [LLISelected, setLLISelected] = useState<'LEFT' | 'RIGHT' | null>(
    value?.LLI?.side ? (value?.LLI?.side as 'LEFT' | 'RIGHT') : null
  );
  const [showLLI, setShowLLI] = useState(false);
  const [LLIAmount, setLLIAmount] = useState(value?.LLI?.value || 0);
  const [LLIError, setLLIError] = useState<string | null>(null);

  // Sync local LLI state from external changes (e.g. Yjs sync from another window).
  // useState only reads value on mount; this keeps it in sync with prop updates.
  useEffect(() => {
    const incomingSide = value?.LLI?.side as 'LEFT' | 'RIGHT' | undefined;
    const incomingAmount = value?.LLI?.value || 0;
    setLLISelected(incomingSide || null);
    setLLIAmount(incomingAmount);
  }, [value?.LLI?.side, value?.LLI?.value]);

  const selectPart = (part: SpineParts) => {
    if (signed) return;

    if (value[part]) {
      setSelected(null);
      onChange(part, SpineSide.NONE);

      return;
    }

    setSelected(part);
    onChange(part, SpineSide.BOTH);
  };

  const selectSide = (part: SpineParts, side: SpineSide) => {
    if (signed) return;
    onChange(part, side);
    setSelected(null);
  };

  const getClassNames = (side: 'LEFT' | 'RIGHT') => {
    return cn(
      `px-4 py-1 border border-gray-400 cursor-pointer dark:border-dark-gray-600 dark:hover:bg-dark-gray-400 dark:bg-dark-gray-800`,
      LLISelected === side
        ? 'bg-primary-500 text-white dark:bg-primary-500 dark:text-white'
        : ' hover:bg-gray-200 bg-white dark:bg-dark-gray-800 dark:hover:bg-dark-gray-400',
      side === 'RIGHT' ? 'rounded-r-lg' : 'rounded-l-lg border-r-0 '
    );
  };

  const getLLIError = () => {
    if (LLISelected && !LLIAmount) {
      setLLIError('Please enter a value');

      return true;
    }

    if (LLIAmount && LLISelected === null) {
      setLLIError('Please select a side');

      return true;
    }

    setLLIError(null);
    setShowLLI(false);
    onChange(
      'LLI' as SpineParts,
      LLISelected === 'LEFT'
        ? SpineSide.LEFT
        : LLISelected === 'RIGHT'
          ? SpineSide.RIGHT
          : undefined,
      +LLIAmount
    );

    return false;
  };

  const handleLLIClick = (side: 'LEFT' | 'RIGHT') => {
    if (LLISelected === side) {
      setLLIAmount(0);
      setLLISelected(null);
    } else {
      setLLISelected(side);
    }
  };

  return (
    <div className="lg:flex flex-row gap-24 w-full mx-auto justify-center relative">
      <div className="text-xl sm:text-2xl font-medium text-gray-500">
        <div className="w-auto flex flex-col py-6">
          <div className="flex flex-row justify-center relative w-auto h-[65vh]">
            <svg
              viewBox="0 0 644.6 1976.6"
              className="spine-picker"
              version="1.1"
              style={{
                height: '100%',
                width: 'auto',
              }}
            >
              <defs>
                <linearGradient id={`right-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: 'transparent', stopOpacity: 1 }} />
                  <stop offset="35%" style={{ stopColor: 'transparent', stopOpacity: 1 }} />
                  <stop
                    offset="100%"
                    style={{
                      stopColor: 'rgba(147, 204, 104, 0.7)',
                      stopOpacity: 1,
                    }}
                  />
                </linearGradient>
                <linearGradient id={`left-${id}`} x1="100%" y1="0%" x2="0%" y2="0%">
                  <stop offset="0%" style={{ stopColor: 'transparent', stopOpacity: 1 }} />
                  <stop offset="35%" style={{ stopColor: 'transparent', stopOpacity: 1 }} />
                  <stop
                    offset="100%"
                    style={{
                      stopColor: 'rgba(147, 204, 104, 0.7)',
                      stopOpacity: 1,
                    }}
                  />
                </linearGradient>
                <linearGradient id={`both-${id}`} x1="100%" y1="0%" x2="0%" y2="0%">
                  <stop
                    offset="0%"
                    style={{
                      stopColor: 'rgba(147, 204, 104, 0.7)',
                      stopOpacity: 1,
                    }}
                  />
                  <stop
                    offset="50%"
                    style={{
                      stopColor: 'rgba(147, 204, 104, 0.3)',
                      stopOpacity: 1,
                    }}
                  />
                  <stop
                    offset="100%"
                    style={{
                      stopColor: 'rgba(147, 204, 104, 0.7)',
                      stopOpacity: 1,
                    }}
                  />
                </linearGradient>
              </defs>
              <image xlinkHref={spine} />
              {spineParts
                ?.filter(part => part.part !== SpineParts.LLI)
                ?.map((part, i) => (
                  <path
                    key={i}
                    className={cn(
                      'relative',
                      'cursor-pointer',
                      selected === part.part ? 'selected' : '',
                      value[part.part] === SpineSide.BOTH
                        ? 'selected-both'
                        : value[part.part] === SpineSide.LEFT
                          ? 'selected-left'
                          : value[part.part] === SpineSide.RIGHT
                            ? 'selected-right'
                            : 'not-selected'
                    )}
                    onClick={e => {
                      selectPart(part.part);
                    }}
                    fill={
                      value[part.part] === SpineSide.LEFT
                        ? `url('#left-${id}')`
                        : value[part.part] === SpineSide.RIGHT
                          ? `url('#right-${id}')`
                          : value[part.part] === SpineSide.BOTH
                            ? `url('#both-${id}')`
                            : 'transparent'
                    }
                    // fill="transparent"
                    data-cy={i}
                    d={part.d}
                  />
                ))}
            </svg>
          </div>
        </div>
      </div>
      {!!selected && (
        <>
          <button
            className="w-10 absolute dark:text-dark-gray-50"
            style={{
              left: '25%',
              top: spinePartsTopPercent[selected],
            }}
            onClick={() => selectSide(selected, SpineSide.LEFT)}
          >
            L
          </button>
          <button
            className="w-10 absolute dark:text-dark-gray-50"
            style={{
              right: '25%',
              top: spinePartsTopPercent[selected],
            }}
            onClick={() => selectSide(selected, SpineSide.RIGHT)}
          >
            R
          </button>
        </>
      )}
      {!hideLegLengthInequality && (
        <div
          onClick={() => {
            if (!showLLI) setShowLLI(true);
          }}
          className={`absolute ${
            showLLI
              ? 'text-gray-400 cursor-not-allowed opacity-50'
              : 'text-primary-500 cursor-pointer hover:text-primary-300'
          }`}
          style={{ right: '3%', top: '98%' }}
        >
          {`Leg Length Inequality${
            !showLLI
              ? ` ${LLISelected ? titleCase(LLISelected) : ''} ${LLIAmount ? `${LLIAmount}mm` : ''}`
              : ''
          } `}
        </div>
      )}
      <Modal isOpen={showLLI} close={() => getLLIError()}>
        <div className="bg-white  text-gray-700 dark:text-dark-gray-100 font-medium grid-cols-4 gap-4 z-10 dark:bg-dark-gray-800">
          <div className="mb-6 sm:mb-0 w-full flex items-center col-span-4">
            <h3 className="text-sm leading-6 ">Leg Length Inequality</h3>
            <div className="flex items-center bg-white p-2 rounded-lg text-xs dark:bg-dark-gray-800 dark:text-dark-gray-100">
              <div
                className={getClassNames('LEFT')}
                onClick={() => {
                  handleLLIClick('LEFT');
                }}
              >
                L
              </div>

              <div
                className={getClassNames('RIGHT')}
                onClick={() => {
                  handleLLIClick('RIGHT');
                }}
              >
                R
              </div>
            </div>
          </div>
          <InputMasked
            placeholder="0.0"
            name="lli"
            onChange={e => {
              setLLIAmount(e);
            }}
            value={LLIAmount}
            numericOptions={{
              decimalScale: 1,
              fixedDecimalScale: true,
              allowNegative: false,
            }}
            hint="in millimeters"
            disabled={LLISelected === null}
          />
          {!!LLIError && <span className="text-red-500 text-sm">{LLIError}</span>}
        </div>
      </Modal>
    </div>
  );
};
